<script setup lang="ts">
const TRANSITION_DURATION_MS = 200;
const HIDE_DURATION_MS = 7000;

let hideTimeout: number;

interface Props {
  variant?: 'success' | 'warning';
  hideDuration?: number;
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'warning',
  hideDuration: HIDE_DURATION_MS,
});

const state = defineModel<boolean>({ default: true });

const onClose = () => {
  state.value = false;
};

watch(
  state,
  () => {
    clearTimeout(hideTimeout);

    hideTimeout = window.setTimeout(onClose, props.hideDuration);
  },
  {},
);
</script>

<template>
  <Transition name="snack">
    <div v-if="state" ref="snackbar" class="snackbar">
      <div class="content-border">
        <div
          class="content-wrapper"
          :class="{
            success: props.variant === 'success',
            warning: props.variant === 'warning',
          }"
        >
          <div class="content-box">
            <div class="text-wrapper">
              <span class="title font-subtitle">
                <slot name="title" />
              </span>
              <span class="text font-bodytext">
                <slot name="text" />
              </span>
            </div>
          </div>
          <button class="close-button" @click="onClose">
            <base-icons-close />
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.snackbar {
  width: 100%;
  max-width: 420px;
  display: flex;
  flex-direction: column;
  position: fixed;
  bottom: 0;
  right: 0;
  min-height: 110px;
  z-index: 2;
  padding: 0 40px 40px 0;
  transition: transform v-bind(TRANSITION_DURATION_MS + 'ms') ease-out;
}

.content-border {
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  background: var(--grey-gradient);
  padding: 1px;
}

.content-wrapper {
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  border-radius: 4px;
  padding: 11px;
  background-color: var(--v2-bg);
}

.content-box {
  display: flex;
  flex-direction: row;
  align-items: center;
}

.text-wrapper {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.title {
  text-transform: uppercase;
  margin-bottom: 4px;
}

.warning .title {
  color: var(--danger);
}

.close-button {
  outline: none;
  border: none;
  background: none;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 0;
}
.close-button,
.close-button span {
  cursor: pointer;
}

.snack-enter-from,
.snack-leave-to {
  transform: translateY(100%);
}

@media (max-width: 1200px) {
  .snackbar {
    max-width: 400px;
    padding: 0 20px 40px 0;
  }
}

@media (max-width: 1023px) {
  .snackbar {
    padding: 0 20px 32px 0;
  }
}

@media (max-width: 743px) {
  .snackbar {
    max-width: 356px;
    padding: 0 20px 32px 0;
    min-height: 100px;
  }
}
</style>
